// @ts-nocheck
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { OrganizationUsageProvider } from './contexts/OrganizationUsageContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { WidgetProvider } from './contexts/WidgetContext';
import { initializeLanguage } from './components/ui/navigation/LanguageSwitcher';
import type { Language } from './config/localization';
import PageLoadingFallback from './components/ui/PageLoadingFallback';
import ChunkErrorBoundary from './components/ui/ChunkErrorBoundary';
import GalleryPage from './components/pages/gallery/GalleryPage';
import { initSentry } from './utils/sentry';
import { lazyWithRetry } from './utils/lazyWithRetry';
import './index.css';

// Initialize Sentry before React renders (no-op if VITE_SENTRY_DSN is not set)
initSentry();

// Lazy-loaded page components
const LandingPage = lazyWithRetry(() => import('./components/pages/LandingPage'));
const WorkflowPage = lazyWithRetry(() => import('./components/pages/WorkflowPage'));
const SetupPage = lazyWithRetry(() => import('./components/pages/user/SetupPage'));
const UserHistoryPage = lazyWithRetry(() => import('./components/pages/user/UserHistoryPage'));
const GeneralTermsPage = lazyWithRetry(() => import('./components/pages/GeneralTermsPage'));
const PrivacyNoticePage = lazyWithRetry(() => import('./components/pages/PrivacyNoticePage'));
const PricingPage = lazyWithRetry(() => import('./components/pages/PricingPage'));
const McpDocsPage = lazyWithRetry(() => import('./components/pages/McpDocsPage'));

// Multi (parallel multi-model generation)
const MultiGenerationPage = lazyWithRetry(() => import('./components/pages/multi/MultiGenerationPage'));

// MCP OAuth pages
const McpAuthorizePage = lazyWithRetry(() => import('./components/pages/mcp/AuthorizePage'));

// Admin pages
const AdminDashboard = lazyWithRetry(() => import('./components/pages/admin/AdminDashboard'));
const AdminUsersPage = lazyWithRetry(() => import('./components/pages/admin/AdminUsersPage'));
const AdminOrgsPage = lazyWithRetry(() => import('./components/pages/admin/AdminOrgsPage'));
const AdminPromptsPage = lazyWithRetry(() => import('./components/pages/admin/AdminPromptsPage'));
const AdminPromptsV2Page = lazyWithRetry(() => import('./components/pages/admin/AdminPromptsV2Page'));
const AdminPromptCategoriesPage = lazyWithRetry(() => import('./components/pages/admin/AdminPromptCategoriesPage'));
const AdminWorkflowsPage = lazyWithRetry(() => import('./components/pages/admin/AdminWorkflowsPage'));
const AdminWidgetsPage = lazyWithRetry(() => import('./components/pages/admin/AdminWidgetsPage'));
const AdminWidgetFieldsPage = lazyWithRetry(() => import('./components/pages/admin/AdminWidgetFieldsPage'));
const AdminApiKeysPage = lazyWithRetry(() => import('./components/pages/admin/AdminApiKeysPage'));
const WorkflowEditPage = lazyWithRetry(() => import('./components/pages/admin/WorkflowEditPage'));
const AdminDebugPage = lazyWithRetry(() => import('./components/pages/admin/AdminDebugPage'));
const AdminBillingPage = lazyWithRetry(() => import('./components/pages/admin/AdminBillingPage'));
const AdminPackagesPage = lazyWithRetry(() => import('./components/pages/admin/AdminPackagesPage'));
const AdminAllRefundsPage = lazyWithRetry(() => import('./components/pages/admin/AdminAllRefundsPage'));
const AdminServiceKeysPage = lazyWithRetry(() => import('./components/pages/admin/AdminServiceKeysPage'));

// Функция для получения локализации Clerk в зависимости от языка
const getClerkLocalization = (language: Language) => {
  const localizations = {
    ru: {
      signIn: {
        start: {
          title: 'Войти в LoreMax AI',
          subtitle: '',
        },
      },
      signUp: {
        start: {
          title: 'Регистрация в LoreMax AI',
          subtitle: 'Создайте аккаунт для начала работы',
        },
      },
    },
    en: {
      signIn: {
        start: {
          title: 'Sign in to LoreMax AI',
          subtitle: '',
        },
      },
      signUp: {
        start: {
          title: 'Sign up for LoreMax AI',
          subtitle: 'Create your account to get started',
        },
      },
    }
  };
  
  return localizations[language] || localizations.en;
};

// Получаем начальный язык
const initialLanguage = initializeLanguage();
const customLocalization = getClerkLocalization(initialLanguage);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <div style={{display:'none'}}>{console.log('🚀 REACT APP STARTING')}</div>
    <ClerkProvider 
      publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
      localization={customLocalization}
      appearance={{
        baseTheme: 'dark',
        variables: {
          colorPrimary: '#8b5cf6',
          colorBackground: '#0f172a',
          colorText: '#ffffff',
          colorInputBackground: '#1e293b',
          colorInputText: '#ffffff',
          borderRadius: '0.75rem',
          fontFamily: '"Inter", system-ui, sans-serif',
        },
        elements: {
          // Основная карточка попапа
          card: 'bg-slate-900/95 backdrop-blur-xl border border-purple-500/30 shadow-2xl rounded-xl',
          
          // Заголовки
          headerTitle: 'text-white text-2xl font-bold',
          headerSubtitle: 'text-purple-200 text-sm',
          
          // Кнопки
          formButtonPrimary: 'bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105',
          
          // Социальные кнопки (Google, и т.д.)
          socialButtonsBlockButton: 'border-purple-500/50 hover:bg-purple-500/20 hover:border-purple-400 text-white rounded-lg transition-all duration-200',
          socialButtonsBlockButtonText: 'text-white font-medium',
          
          // Поля ввода
          formFieldLabel: 'text-purple-200 font-medium text-sm',
          formFieldInput: 'bg-slate-800/70 border-purple-500/40 text-white placeholder-gray-400 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-500/20 transition-all',
          
          // Футер
          footerActionText: 'text-purple-200',
          footerActionLink: 'text-purple-400 hover:text-purple-300 font-medium underline-offset-4 hover:underline',
          
          // Дополнительные элементы
          modalBackdrop: 'backdrop-blur-sm',
          modalContent: 'rounded-2xl shadow-2xl',
          dividerLine: 'bg-purple-500/30',
          dividerText: 'text-purple-300',
          
          // Ошибки
          formFieldErrorText: 'text-red-400',
          alertError: 'bg-red-900/50 border-red-500/50 text-red-200',
        }
      }}
    >
      <BrowserRouter>
        <div style={{display:'none'}}>{console.log('🌍 BROWSER ROUTER LOADED')}</div>
        <OrganizationProvider>
          <OrganizationUsageProvider>
          <SidebarProvider>
            <WidgetProvider>
              <ChunkErrorBoundary>
              <Suspense fallback={<PageLoadingFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/app" element={<WorkflowPage />} />
                <Route path="/image" element={<WorkflowPage />} />
                <Route path="/multi" element={<MultiGenerationPage />} />
                <Route path="/video" element={<WorkflowPage />} />
                <Route path="/audio" element={<WorkflowPage />} />
                <Route path="/text" element={<WorkflowPage />} />
                <Route path="/3d" element={<WorkflowPage />} />
                <Route path="/setup" element={<SetupPage />} />

                {/* Organization routes - more specific first */}
                <Route path="/organization/manager/history/user/:userId" element={<UserHistoryPage />} />
                <Route path="/organization/manager/history" element={<WorkflowPage />} />
                <Route path="/organization/manager/categories" element={<WorkflowPage />} />
                <Route path="/organization/my-references" element={<WorkflowPage />} />
                <Route path="/organization/manager/corp-prompts" element={<WorkflowPage />} />
                <Route path="/organization/manager/prompts" element={<WorkflowPage />} />
                <Route path="/organization/manager" element={<WorkflowPage />} />
                <Route path="/organization/mcp" element={<WorkflowPage />} />
                <Route path="/organization/billing" element={<WorkflowPage />} />
                <Route path="/organization/collections" element={<WorkflowPage />} />
                <Route path="/organization/info" element={<WorkflowPage />} />

                {/* Admin routes - specific routes only */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/workflows/:workflowId/edit" element={<WorkflowEditPage />} />
                <Route path="/admin/workflows" element={<AdminWorkflowsPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/organizations" element={<AdminOrgsPage />} />
                <Route path="/admin/organizations/:id/api-keys" element={<AdminApiKeysPage />} />
                <Route path="/admin/prompts" element={<AdminPromptsPage />} />
                <Route path="/admin/prompts-v2" element={<AdminPromptsV2Page />} />
                <Route path="/admin/prompt-categories" element={<AdminPromptCategoriesPage />} />
                <Route path="/admin/widgets" element={<AdminWidgetsPage />} />
                <Route path="/admin/widgets/:id/fields" element={<AdminWidgetFieldsPage />} />
                <Route path="/admin/debug" element={<AdminDebugPage />} />
                <Route path="/admin/billing" element={<AdminBillingPage />} />
                <Route path="/admin/refunds" element={<AdminAllRefundsPage />} />
                <Route path="/admin/packages" element={<AdminPackagesPage />} />
                <Route path="/admin/api-keys" element={<AdminServiceKeysPage />} />

                {/* Settings, Help and Gallery routes */}
                <Route path="/profile" element={<WorkflowPage />} />
                <Route path="/settings" element={<WorkflowPage />} />
                <Route path="/help" element={<WorkflowPage />} />
                <Route path="/gallery" element={<GalleryPage />} />

                {/* MCP OAuth consent page */}
                <Route path="/mcp/authorize" element={<McpAuthorizePage />} />

                {/* Legal and public routes */}
                <Route path="/terms" element={<GeneralTermsPage />} />
                <Route path="/privacy" element={<PrivacyNoticePage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/mcp-docs" element={<McpDocsPage />} />
              </Routes>
              </Suspense>
              </ChunkErrorBoundary>
            </WidgetProvider>
          </SidebarProvider>
          </OrganizationUsageProvider>
        </OrganizationProvider>
      </BrowserRouter>
    </ClerkProvider>
  </React.StrictMode>
); 